import { plugin, debug } from 'light-component';
import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { Space, Select as ASelect } from 'antd';
import { Block } from '../../block';
import { t } from '../../i18n';
import { Require, Select } from '../component';
import { useBreakpoint } from '../../../hooks/useBreakpoint';
import styles from './style.module.less';
import type { FormVersionProps, VersionOptions } from './types';

export function WidgetFormVersion(props: FormVersionProps): ReactElement {
  debug.widget('dashboard formVersion', props);
  const { md } = useBreakpoint();

  const { isSingle = false } = props;
  const [formValue, setFormValue] = useState<string[]>([]);
  const [singleFormValue, setSingleFormValue] = useState<string | null>(null);
  const [versionValue, setVersionValue] = useState<string[]>([]);
  const [curVersionOptions, setCurVersionOptions] = useState<VersionOptions[]>([]);
  const selectChange = useRef(false);

  const formOptions = useMemo(() => {
    if (props.formOptions?.length > 0) {
      let options = props.formOptions?.map(item => ({
        value: item.value,
        label: item.title
      }));
      const uniqueValue = new Set(options?.map(item => item.value));
      options = options?.filter(item => uniqueValue.delete(item.value));
      options.sort((itemA, itemB) => itemA.label.localeCompare(itemB.label));
      return options;
    }
    return [];
  }, [props.formOptions]);

  const versionOptions = useMemo(() => {
    if (props.versionOptions?.length > 0) {
      let options = props.versionOptions?.map(item => ({
        value: item.value,
        formId: item.formId,
        label: t(item.label)
      }));
      const uniqueValue = new Set(options?.map(item => item.value));
      options = options?.filter(item => uniqueValue.delete(item.value));
      return options;
    }
    return [];
  }, [props.versionOptions]);

  useEffect(() => {
    setCurVersionOptions(versionOptions.filter(item => {
      if (!isSingle) {
        return formValue?.includes(item.formId);
      }
      return singleFormValue === item.formId;
    }));
  }, [versionOptions, formValue, singleFormValue]);

  useEffect(() => {
    if (selectChange.current) {
      selectChange.current = false;
      return;
    }
    if (props.value != null) {
      if (!isSingle) {
        const formValue = new Set<string>([]);
        versionOptions.forEach(item => {
          if (props.value.includes(item.value)) {
            formValue.add(item.formId);
          }
        });
        setFormValue([...formValue]);
      } else {
        let formValue = null;
        versionOptions.forEach(item => {
          if (props.value.includes(item.value)) {
            formValue = item.formId;
          }
        });
        setSingleFormValue(formValue);
      }
      setVersionValue([...props.value]);
    } else {
      setFormValue([]);
      setSingleFormValue(null);
      setVersionValue([]);
    }
  }, [props.value]);

  const handleFormChange = (newValue: string[]): void => {
    selectChange.current = true;
    const value = versionOptions.filter(item => newValue.includes(item.formId)).map(item => item.value);
    if (newValue?.length === 1) {
      setVersionValue([...value]);
    } else {
      setVersionValue([]);
    }
    setFormValue(newValue);
    props.onChange?.(value);
  };

  const handleSingleFormChange = (newValue: string): void => {
    selectChange.current = true;
    const value = versionOptions.filter(item => newValue === item.formId).map(item => item.value);
    setVersionValue([...value]);
    setSingleFormValue(newValue);
    props.onChange?.(value);
  };

  const handleVersionChange = (newValue: string[]): void => {
    selectChange.current = true;
    if (newValue.length === 0) {
      const value = versionOptions.filter(item => {
        if (isSingle) {
          return singleFormValue === item.formId;
        }
        return formValue.includes(item.formId);
      }).map(item => item.value);

      props.onChange?.(value);
    } else {
      props.onChange?.(newValue);
    }
    setVersionValue(newValue);
  };

  return (
    <div className={props.className ?? styles.propertyItem}>
      <div className={styles.input}>
        <Require require={props.require} />
        <span className={styles.title}>{t(props.title)}</span>
      </div>
      <Space>
        <Block if={!isSingle}>
          <Select
            className={styles.formSelectBox}
            value={formValue}
            placeholder={t(props.placeholder ?? '')}
            options={formOptions}
            allowClear
            onChange={handleFormChange}
          />
        </Block>
        <Block if={isSingle}>
          <ASelect
            className={styles.formSelectBox}
            value={singleFormValue}
            placeholder={t(props.placeholder ?? '')}
            options={formOptions}
            allowClear
            showSearch
            optionFilterProp='label'
            onChange={handleSingleFormChange}
            popupMatchSelectWidth={false}
            dropdownStyle={md === false ? { maxWidth: 300 } : { maxWidth: 600 }}
          />
        </Block>
        <Block if={formValue?.length === 1 || singleFormValue !== null}>
          <Select
            className={styles.formSelectBox}
            value={versionValue}
            placeholder={t('选择版本')}
            options={curVersionOptions}
            allowClear
            onChange={handleVersionChange}
          />
        </Block>
      </Space>
    </div>
  );
}

plugin.register({ kind: 'widget', type: 'formVersion', component: WidgetFormVersion });
